import type {
  BaseEventOrigin,
  TrackViewArticle,
  TrackViewListing,
  TrackViewPage,
  TrackViewSalesPoster,
  ViewArticleInputObject,
  ViewSalesPosterInputObject,
} from '@schibsted/pulse-news-media';
import type { CuratePulseTrackingCustomProperties } from '../../core/utils/types.js';
import type {
  ByfunnFrapiArticle,
  ByfunnPromoArticle,
} from '../types/frapi-types.js';
import type { TagEntity } from '@schibsted/frapi-schema';

import { getBlendMetrics } from '../../core/mandatory-integrations/pulse/utils/metrics.js';
import { accessLevel, accessLevelName } from '../../core/utils/access.js';
import { getAuthorEmails } from '../views/components/articles/utils/authors.js';
import { makeTagTitle } from '../views/components/articles/helpers.js';

const trackArticlePageView = (
  trackViewArticle: TrackViewArticle,
  article: ByfunnFrapiArticle,
) => {
  const imageUrl = article.mainImage
    ? `https://premium.vgc.no/ap/images/${article.mainImage.id}?fit=crop&h=878&q=80&tight=true&w=2048`
    : 'https://cdn.aftenposten.no/vink/images/wordmark.png';

  const object: ViewArticleInputObject = {
    articleId: article.id,
    accessLevel: accessLevel(article.access),
    accessLevelName: accessLevelName(article.access),
    authors: getAuthorEmails(article),
    category: article.section,
    name: article.title,
    tags: article.tags.map((t) => t.title),
    wordCount: article.wordCount,
    'spt:custom': {
      'spt:wordCount': article.wordCount,
      'spt:authors': getAuthorEmails(article),
      'spt:published': article.published,
      'spt:update': article.updated,
      'spt:imageUrl': article?.mainImage && imageUrl,
    },
  };

  trackViewArticle({ object });
};

const trackFrontpageView = (
  trackViewListing: TrackViewListing,
  personalization: CuratePulseTrackingCustomProperties,
) => {
  trackViewListing({
    object: {
      id: 'frontpage',
      name: 'Frontpage - Vink',
    },
    metrics: getBlendMetrics(personalization.variant, personalization),
  }).then((data) => data);
};

const trackSectionPageView = (
  trackViewListing: TrackViewListing,
  sectionName: string,
  personalization?: CuratePulseTrackingCustomProperties,
) => {
  if (!personalization) {
    trackViewListing({
      object: {
        id: 'section-page',
        name: sectionName,
      },
    }).then((data) => data);
  } else {
    trackViewListing({
      object: {
        id: 'section-page',
        name: sectionName,
      },
      metrics: getBlendMetrics(personalization.variant, personalization),
    }).then((data) => data);
  }
};

const trackStaticPageView = (
  trackViewPage: TrackViewPage,
  componentName: string,
  custom?: Record<string, unknown>,
) => {
  trackViewPage({
    object: {
      pageId: componentName === 'error' ? 'error:404' : componentName,
      name: `Page - ${componentName}`,
      'spt:custom': custom,
    },
  }).then((data) => data);
};

const trackSearchPageView = (
  trackViewListing: TrackViewListing,
  currentUrl: string,
) => {
  const query = currentUrl.match('phrase=(.*)&')?.[1] || currentUrl;

  trackViewListing({
    object: {
      id: 'search',
      name: 'Search Result Page',
      'spt:custom': {
        query,
      },
    },
  }).then((data) => data);
};

const trackTagPageView = (
  trackViewListing: TrackViewListing,
  currentUrl: string,
  tagSlug: string,
) => {
  trackViewListing({
    object: {
      id: 'tag',
      name: tagSlug,
      'spt:custom': {
        currentUrl,
      },
    },
  });
};

const trackTagCollectionPageView = (
  trackViewListing: TrackViewListing,
  sectionName: string,
  tags: TagEntity[],
) => {
  const tagTitles = tags.map((tag) => makeTagTitle(tag.title));

  trackViewListing({
    object: {
      id: `tag:${sectionName}`,
      name: `tag:${sectionName}`,
      'spt:custom': {
        tags: tagTitles,
      },
    },
  });
};

const trackAuthorBioPageView = (
  trackViewListing: TrackViewListing,
  currentUrl: string,
  authorSlug: string,
) => {
  trackViewListing({
    object: {
      id: 'tag',
      name: authorSlug,
      'spt:custom': {
        currentUrl,
      },
    },
  });
};

const trackStoryPageView = (
  trackViewListing: TrackViewListing,
  currentUrl: string,
  storyName: string,
) => {
  trackViewListing({
    object: {
      id: 'story',
      name: storyName,
      'spt:custom': {
        currentUrl,
      },
    },
  }).then((data) => data);
};

const trackSalesposterView = (
  trackViewSalesPoster: TrackViewSalesPoster,
  article: ByfunnPromoArticle,
  origin: BaseEventOrigin,
) => {
  const object: ViewSalesPosterInputObject = {
    articleId: article.id,
    accessLevel: 'Paid',
    accessLevelName: 'Paid',
    authors: getAuthorEmails(article),
    category: article.section,
    name: article.title,
    tags: article.tags.map((t) => t.title),
    wordCount: article.wordCount,
    salesPosterType: 'Paid',
    'spt:custom': {
      'spt:published': article.published,
      'spt:update': article.updated,
    },
  };

  trackViewSalesPoster({
    object,
    origin,
  });
};

export {
  trackArticlePageView,
  trackFrontpageView,
  trackSalesposterView,
  trackSearchPageView,
  trackSectionPageView,
  trackStaticPageView,
  trackStoryPageView,
  trackTagPageView,
  trackTagCollectionPageView,
  trackAuthorBioPageView,
};
